import { useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { submitToGoogleForm } from '../lib/googleForms';
import { Heart, Send, Sparkles } from 'lucide-react';

// Nature theme options with translations
const natureThemeOptions = [
  { id: 'water', en: 'Water themes', es: 'Temas de agua' },
  { id: 'animals', en: 'Wild animal personalities', es: 'Personalidades de animales salvajes' },
  { id: 'insects', en: 'The secret life of insects', es: 'La vida secreta de los insectos' },
  { id: 'fungi', en: 'The magic of fungi and microbes', es: 'La magia de los hongos y microbios' },
  { id: 'earth-care', en: 'Caring for the Earth', es: 'Cuidar la Tierra' },
  { id: 'other', en: 'Other', es: 'Otro' },
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { language } = useLanguage();

  // Form state
  const [relationship, setRelationship] = useState('');
  const [childAge, setChildAge] = useState('');
  const [enjoymentRating, setEnjoymentRating] = useState('');
  const [difficultyRating, setDifficultyRating] = useState('');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [childLoves, setChildLoves] = useState('');
  const [improvements, setImprovements] = useState('');
  const [childAsked, setChildAsked] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleTheme = (themeId: string) => {
    setSelectedThemes(prev =>
      prev.includes(themeId)
        ? prev.filter(id => id !== themeId)
        : [...prev, themeId]
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Submit to Google Form
      await submitToGoogleForm({
        relationship,
        childAge,
        enjoymentRating,
        difficultyRating,
        selectedThemes,
        childLoves,
        improvements,
        childAsked,
        email,
        language,
      });

      // Show success message
      toast.success(
        language === 'en'
          ? '✅ Thank you for your feedback!'
          : '✅ ¡Gracias por tus comentarios!'
      );

      // Reset form
      setRelationship('');
      setChildAge('');
      setEnjoymentRating('');
      setDifficultyRating('');
      setSelectedThemes([]);
      setChildLoves('');
      setImprovements('');
      setChildAsked('');
      setEmail('');

      // Close modal after a brief delay
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(
        language === 'en'
          ? '❌ Error submitting feedback. Please try again.'
          : '❌ Error al enviar comentarios. Por favor, inténtalo de nuevo.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = relationship.trim().length > 0 || childAge.trim().length > 0;

  const t = {
    en: {
      title: 'Help Us Improve!',
      subtitle: 'Your feedback shapes future nature adventures for young learners',
      q1: 'What is your relationship to the child?',
      q1Options: {
        parent: 'Parent/Guardian',
        grandparent: 'Grandparent',
        teacher: 'Teacher/Educator',
        caregiver: 'Caregiver',
        other: 'Other',
      },
      q2: 'Child\'s age',
      q3: 'How did your child like helping Benny get home?',
      q3Helper: '1 = Not at all, 5 = Loved it!',
      q4: 'How easy was the game for your child to play?',
      q4Options: {
        easy: 'Very easy',
        justRight: 'Just right',
        hard: 'Too difficult',
      },
      q5: 'What nature themes would your child enjoy exploring next?',
      q5Helper: 'Select all that interest them',
      q6: 'Tell us what your child loves about nature or animals!',
      q6Placeholder: 'Share what excites them... (optional)',
      q7: 'How can we make future games better for your child?',
      q7Placeholder: 'Ideas, suggestions, improvements... (optional)',
      q8: 'One thing your child asked for while playing',
      q8Placeholder: 'If they mentioned anything... (optional)',
      q9: 'Would you like updates when we add new activities?',
      q9Placeholder: 'Your email address (optional)',
      cancel: 'Cancel',
      submit: 'Send Feedback',
      sending: 'Sending...',
      thankYou: '💚 Thank you for helping us create better eco-learning experiences!',
    },
    es: {
      title: '¡Ayúdanos a Mejorar!',
      subtitle: 'Tus comentarios dan forma a futuras aventuras de naturaleza para pequeños aprendices',
      q1: '¿Cuál es tu relación con el niño/niña?',
      q1Options: {
        parent: 'Padre/Madre/Tutor',
        grandparent: 'Abuelo/Abuela',
        teacher: 'Maestro/Educador',
        caregiver: 'Cuidador',
        other: 'Otro',
      },
      q2: 'Edad del niño/niña',
      q3: '¿Cuánto le gustó a tu hijo/a ayudar a Beni a llegar a casa?',
      q3Helper: '1 = Para nada, 5 = ¡Le encantó!',
      q4: '¿Qué tan fácil fue el juego para tu hijo/a?',
      q4Options: {
        easy: 'Muy fácil',
        justRight: 'Justo',
        hard: 'Demasiado difícil',
      },
      q5: '¿Qué temas de naturaleza le gustaría explorar a tu hijo/a a continuación?',
      q5Helper: 'Selecciona todos los que le interesen',
      q6: '¡Cuéntanos qué le encanta a tu hijo/a sobre la naturaleza o los animales!',
      q6Placeholder: 'Comparte qué le emociona... (opcional)',
      q7: '¿Cómo podemos hacer mejores juegos futuros para tu hijo/a?',
      q7Placeholder: 'Ideas, sugerencias, mejoras... (opcional)',
      q8: 'Una cosa que tu hijo/a pidió mientras jugaba',
      q8Placeholder: 'Si mencionó algo... (opcional)',
      q9: '¿Te gustaría recibir actualizaciones cuando agreguemos nuevas actividades?',
      q9Placeholder: 'Tu dirección de correo electrónico (opcional)',
      cancel: 'Cancelar',
      submit: 'Enviar Opinión',
      sending: 'Enviando...',
      thankYou: '💚 ¡Gracias por ayudarnos a crear mejores experiencias de aprendizaje ecológico!',
    },
  };

  const text = t[language];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
        <DialogHeader>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-pink-500" />
            <DialogTitle className="text-orange-900">
              {text.title}
            </DialogTitle>
            <Heart className="w-5 h-5 text-pink-500" />
          </div>
          <DialogDescription className="text-center text-gray-700">
            {text.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Question 1: Relationship */}
          <div>
            <Label className="block mb-3 text-orange-900 font-semibold">
              {text.q1}
            </Label>
            <RadioGroup value={relationship} onValueChange={setRelationship}>
              <div className="space-y-2">
                {Object.entries(text.q1Options).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <RadioGroupItem value={label} id={`rel-${key}`} />
                    <Label htmlFor={`rel-${key}`} className="cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Question 2: Child Age */}
          <div>
            <Label className="block mb-3 text-orange-900 font-semibold">
              {text.q2}
            </Label>
            <RadioGroup value={childAge} onValueChange={setChildAge}>
              <div className="flex gap-4 flex-wrap">
                {['3', '4', '5', '6', '7+'].map((age) => (
                  <div key={age} className="flex items-center space-x-2">
                    <RadioGroupItem value={age} id={`age-${age}`} />
                    <Label htmlFor={`age-${age}`} className="cursor-pointer">
                      {age}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Question 3: Enjoyment Rating */}
          <div>
            <Label className="block mb-2 text-pink-900 font-semibold">
              {text.q3}
            </Label>
            <p className="text-base text-gray-500 mb-3">{text.q3Helper}</p>
            <RadioGroup value={enjoymentRating} onValueChange={setEnjoymentRating}>
              <div className="flex gap-4 flex-wrap">
                {['1', '2', '3', '4', '5'].map((rating) => (
                  <div key={rating} className="flex items-center space-x-2">
                    <RadioGroupItem value={rating} id={`enjoy-${rating}`} />
                    <Label htmlFor={`enjoy-${rating}`} className="cursor-pointer">
                      {rating}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Question 4: Difficulty Rating */}
          <div>
            <Label className="block mb-3 text-purple-900 font-semibold">
              {text.q4}
            </Label>
            <RadioGroup value={difficultyRating} onValueChange={setDifficultyRating}>
              <div className="space-y-2">
                {Object.entries(text.q4Options).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <RadioGroupItem value={label} id={`diff-${key}`} />
                    <Label htmlFor={`diff-${key}`} className="cursor-pointer">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Question 5: Nature Themes (Checkboxes) */}
          <div>
            <div className="flex items-start gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <Label className="text-green-900 font-semibold">
                  {text.q5}
                </Label>
                <p className="text-base text-gray-500 mt-1">{text.q5Helper}</p>
              </div>
            </div>
            <div className="bg-white rounded-lg border-2 border-green-200 p-4">
              <div className="space-y-3">
                {natureThemeOptions.map((theme) => (
                  <div key={theme.id} className="flex items-start gap-3">
                    <Checkbox
                      id={theme.id}
                      checked={selectedThemes.includes(theme.id)}
                      onCheckedChange={() => toggleTheme(theme.id)}
                      className="mt-0.5"
                    />
                    <Label htmlFor={theme.id} className="text-lg text-gray-700 cursor-pointer leading-relaxed">
                      {language === 'en' ? theme.en : theme.es}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            {selectedThemes.length > 0 && (
              <div className="text-base text-green-600 mt-2">
                {selectedThemes.length} {language === 'en' ? 'selected' : 'seleccionados'}
              </div>
            )}
          </div>

          {/* Question 6: What child loves */}
          <div>
            <Label className="block mb-2 text-blue-900 font-semibold">
              {text.q6}
            </Label>
            <Textarea
              value={childLoves}
              onChange={(e) => setChildLoves(e.target.value)}
              placeholder={text.q6Placeholder}
              className="min-h-[80px] resize-none bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400"
              maxLength={300}
            />
            <div className="text-base text-gray-400 mt-1 text-right">
              {childLoves.length}/300
            </div>
          </div>

          {/* Question 7: Improvements */}
          <div>
            <Label className="block mb-2 text-purple-900 font-semibold">
              {text.q7}
            </Label>
            <Textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder={text.q7Placeholder}
              className="min-h-[100px] resize-none bg-white border-purple-200 focus:border-purple-400 focus:ring-purple-400"
              maxLength={500}
            />
            <div className="text-base text-gray-400 mt-1 text-right">
              {improvements.length}/500
            </div>
          </div>

          {/* Question 8: What child asked for */}
          <div>
            <Label className="block mb-2 text-pink-900 font-semibold">
              {text.q8}
            </Label>
            <Textarea
              value={childAsked}
              onChange={(e) => setChildAsked(e.target.value)}
              placeholder={text.q8Placeholder}
              className="min-h-[60px] resize-none bg-white border-pink-200 focus:border-pink-400 focus:ring-pink-400"
              maxLength={200}
            />
            <div className="text-base text-gray-400 mt-1 text-right">
              {childAsked.length}/200
            </div>
          </div>

          {/* Question 9: Email opt-in */}
          <div>
            <Label className="block mb-2 text-orange-900 font-semibold">
              {text.q9}
            </Label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={text.q9Placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {text.cancel}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 hover:from-orange-700 hover:via-pink-700 hover:to-purple-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {text.sending}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {text.submit}
                </>
              )}
            </Button>
          </div>

          {/* Thank you note */}
          <div className="pt-4 border-t border-orange-200 text-center">
            <p className="text-base text-gray-500">
              {text.thankYou}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
